import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { DEFAULT_THEME, themes } from '@themes';
import { ITheme, applyTheme, colorLuminance } from '@themes/utils';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

type ThemeModeContextType = {
  mode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setMode: React.Dispatch<ThemeMode>;
  setBrandTheme: React.Dispatch<ITheme>;
};

const STORAGE_KEY = 'themeMode';
const MEDIA_QUERY = '(prefers-color-scheme: dark)';

const defaultBrandTheme = themes[DEFAULT_THEME];

const ThemeModeContext = createContext<ThemeModeContextType | undefined>(undefined);

const getStoredMode = (): ThemeMode => {
  const storedMode = window.localStorage.getItem(STORAGE_KEY);
  return storedMode === 'light' || storedMode === 'dark' || storedMode === 'system'
    ? storedMode
    : 'system';
};

const getSystemTheme = (): ResolvedTheme =>
  window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light';

const createDarkTheme = (theme: ITheme): ITheme => {
  const primary = theme.primary || defaultBrandTheme.primary;
  const secondary = theme.secondary || defaultBrandTheme.secondary;

  return {
    ...theme,
    primary,
    secondary,
    textPrimary: '#f8fafc',
    backgroundPrimary: theme.backgroundPrimary || primary,
    backgroundSecondary: secondary,
    backgroundPrimaryLighten: colorLuminance(theme.backgroundPrimary || primary, -0.1),
    backgroundSecondaryLighten: colorLuminance(secondary, -0.2),
    backgroundHover: '#374151',
  };
};

const ThemeModeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(() => getStoredMode());
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => getSystemTheme());
  const [brandTheme, setBrandTheme] = useState<ITheme>(defaultBrandTheme);

  const resolvedTheme = mode === 'system' ? systemTheme : mode;

  useEffect(() => {
    const mediaQuery = window.matchMedia(MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? 'dark' : 'light');
    };

    setSystemTheme(mediaQuery.matches ? 'dark' : 'light');

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const nextTheme = resolvedTheme === 'dark' ? createDarkTheme(brandTheme) : brandTheme;

    root.dataset.themeMode = mode;
    root.dataset.theme = resolvedTheme;
    root.classList.toggle('dark', resolvedTheme === 'dark');
    applyTheme(nextTheme);
  }, [brandTheme, mode, resolvedTheme]);

  const setMode = (nextMode: ThemeMode) => {
    setModeState(nextMode);
    window.localStorage.setItem(STORAGE_KEY, nextMode);
  };

  const value = useMemo(
    () => ({
      mode,
      resolvedTheme,
      setMode,
      setBrandTheme,
    }),
    [mode, resolvedTheme],
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};

const useThemeMode = () => {
  const context = useContext(ThemeModeContext);

  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }

  return context;
};

export { STORAGE_KEY, ThemeModeProvider, useThemeMode };
